import { useState } from "preact/hooks";
import { QyooImage, Notice, Spinner } from "../components";
import { api } from "../api";
import { minterAbi } from "../contracts";
import { MAX_QYOO_ID, networkMismatch, parseQyooInput } from "../domain";
import type { MintTier, MintingStatus } from "../types";
import type { WalletState } from "../wallet";

export function MintView(props: {
  status: MintingStatus | null;
  wallet: WalletState;
  navigate(path: string): void;
  refreshStatus(): Promise<void>;
}) {
  const [tier, setTier] = useState<MintTier>("random");
  const [customId, setCustomId] = useState("262144");
  const [availability, setAvailability] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const parsedPreview = (() => { try { return parseQyooInput(customId); } catch { return 262144; } })();
  const expectedChain = props.status?.chain.id ?? null;
  const wrongNetwork = networkMismatch(props.wallet.chainId, expectedChain);
  const price = tier === "random" ? props.status?.prices.randomWei : props.status?.prices.customWei;

  async function checkAvailability() {
    setMessage(null);
    setAvailability(null);
    try {
      parseQyooInput(customId);
      const result = await api.availability(customId);
      setAvailability(result.available);
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Availability check failed" });
    }
  }

  async function mint() {
    if (!props.wallet.account || !props.status?.minter) return;
    setBusy(true);
    setMessage(null);
    try {
      if (wrongNetwork && expectedChain) await props.wallet.switchChain(expectedChain);
      const qyoo = tier === "custom" ? String(parseQyooInput(customId)) : undefined;
      const quote = await api.quote(props.wallet.account, tier, qyoo);
      if (quote.chainId !== expectedChain || quote.minter.toLowerCase() !== props.status.minter.toLowerCase()
        || quote.voucher.recipient.toLowerCase() !== props.wallet.account.toLowerCase()
        || (quote.chainId === 11155111 && BigInt(quote.voucher.price) !== 0n)) {
        throw new Error("The beta authorization changed. Refresh and try again; no transaction was sent.");
      }
      const voucher = {
        tokenId: BigInt(quote.voucher.tokenId),
        recipient: quote.voucher.recipient,
        tier: quote.voucher.tier,
        price: BigInt(quote.voucher.price),
        nonce: BigInt(quote.voucher.nonce),
        deadline: BigInt(quote.voucher.deadline),
        pricingVersion: BigInt(quote.voucher.pricingVersion),
        resolverHash: quote.voucher.resolverHash
      } as const;
      const hash = await props.wallet.writeContract({
        chainId: quote.chainId,
        address: quote.minter,
        abi: minterAbi,
        functionName: "redeem",
        args: [voucher, quote.resolver, quote.signature],
        value: voucher.price
      });
      setMessage({ kind: "success", text: `Transaction ${hash.slice(0, 10)}… submitted. Confirming ownership…` });
      await props.wallet.waitForTransaction(hash, quote.chainId);
      setMessage({ kind: "success", text: `Qyoo #${quote.qyoo} is now owned by this wallet.` });
      props.navigate(`/qyoo/${quote.qyoo}`);
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Minting failed" });
    } finally {
      setBusy(false);
      await props.refreshStatus().catch(() => undefined);
    }
  }

  return (
    <div class="page-shell mint-page">
      <header class="page-intro">
        <p class="eyebrow">Sepolia beta · no monetary value</p>
        <h1>Choose your Qyoo.</h1>
        <p>Get an actual test-network Qyoo in your wallet. No real money and no mint fee. Your wallet may need free Sepolia test ETH for the network transaction; never buy funds for this beta.</p>
      </header>
      <div class="mint-layout">
        <section class="panel mint-controls" aria-labelledby="mint-options">
          <h2 id="mint-options">Mint type</h2>
          <div class="segmented" role="group" aria-label="Mint type">
            <button aria-pressed={tier === "random"} onClick={() => setTier("random")}><strong>Random</strong><span>A new number, chosen for you</span></button>
            <button aria-pressed={tier === "custom"} onClick={() => setTier("custom")}><strong>Custom</strong><span>Choose the exact 36-bit ID</span></button>
          </div>
          {tier === "custom" && (
            <div class="field-group">
              <label for="custom-qyoo">Qyoo ID</label>
              <div class="input-action"><input id="custom-qyoo" inputMode="numeric" value={customId} onInput={(event) => { setCustomId(event.currentTarget.value); setAvailability(null); }} aria-describedby="id-help" /><button class="button ghost compact" onClick={() => void checkAvailability()}>Check</button></div>
              <p id="id-help" class="field-help">Decimal, from 0 through {MAX_QYOO_ID.toLocaleString()}.</p>
              {availability !== null && <p class={availability ? "available" : "unavailable"}>{availability ? "Available to mint" : "Already owned"}</p>}
            </div>
          )}
          <div class="price-row"><span>Beta mint fee</span><strong>{price === null || price === undefined ? "Unavailable" : BigInt(price) === 0n ? "Free" : "Beta issuance not ready"}</strong></div>
          {props.status && !props.status.enabled && <Notice kind="info">Beta acquisition is temporarily unavailable. You can still <a href="/demo">try the public examples</a>. <a href="/support">Check setup and support</a>.</Notice>}
          {props.status?.mainnetDisabled && <Notice kind="error">Mainnet transactions are disabled in this release.</Notice>}
          {wrongNetwork && <Notice kind="error">Your wallet needs the Sepolia test network. The next step asks your wallet to switch; no real funds are needed.</Notice>}
          {props.wallet.error && <Notice kind="error">{props.wallet.error}</Notice>}
          {message && <Notice kind={message.kind}>{message.text}</Notice>}
          {!props.wallet.account
            ? <button class="button primary wide" onClick={() => void props.wallet.connect()}>Connect wallet to continue</button>
            : <button class="button primary wide" disabled={busy || !props.status?.enabled || (tier === "custom" && availability === false)} onClick={() => void mint()}>
                {busy ? <Spinner label="Preparing secure mint…" /> : wrongNetwork ? `Switch to ${props.status?.chain.network}` : `Mint ${tier === "random" ? "a random" : `#${customId}`} Qyoo`}
              </button>}
          <p>Use an Ethereum wallet browser or desktop wallet extension. Need test gas? Follow the <a href="/support">free test-network setup guide</a>.</p>
          <p class="legal-note">The issuer authorization is short-lived and bound to this wallet, current price, network, resolver, and a one-use nonce.</p>
        </section>
        <aside class="mint-preview" aria-label="Qyoo preview">
          <div class="preview-stage"><QyooImage qyoo={tier === "custom" ? parsedPreview : 51_173_502_956} /><span>36-bit physical identity</span></div>
          <h2>{tier === "custom" ? `#${parsedPreview.toLocaleString()}` : "Your new Qyoo"}</h2>
          <p>The white border is a recommended presentation quiet zone outside the carrier.</p>
        </aside>
      </div>
    </div>
  );
}
